<template>
  <template v-if="cardAvailable">
    <p :id="questionId" class="fs-14 color-neutral-600 mb-3">How would you like to pay?</p>
    <v-radio-group
      v-model="paymentMode"
      color="success"
      hide-details
      class="payment-options"
      :aria-labelledby="questionId"
    >
      <v-radio
        v-for="option of options"
        :key="option.value"
        :value="option.value"
        class="payment-option"
        :class="{ 'payment-option--selected': paymentMode === option.value }"
      >
        <template #label>
          <span class="d-flex flex-column">
            <span class="payment-option__title font-weight-light-bold">{{ option.title }}</span>
            <span class="fs-14 color-neutral-600">{{ option.subtitle }}</span>
          </span>
        </template>
      </v-radio>
    </v-radio-group>
  </template>

  <!-- NOTE: With no card option there is nothing to choose, so this is a plain row rather than a
       lone radio, which would be the same do-nothing control this picker replaced. -->
  <div v-else class="payment-option payment-option--static d-flex flex-column">
    <span class="payment-option__title font-weight-light-bold">{{ offlineOption.title }}</span>
    <span class="fs-14 color-neutral-600">{{ offlineOption.subtitle }}</span>
  </div>

  <v-sheet v-if="paymentMode === 'offline'" border rounded class="pa-4 mt-4">
    <p class="fs-14 color-neutral-600">{{ billingInstructions }}</p>
  </v-sheet>
</template>

<script setup>
import { useId } from 'vue';

// NOTE: The question is a paragraph rather than the group's label (which would change the
// layout), so aria-labelledby is what names the radiogroup for screen readers.
const questionId = useId();

defineProps({
  billingInstructions: String,
  cardAvailable: { type: Boolean, default: true },
});

const paymentMode = defineModel({ type: String, required: true }); // card | offline

const cardOption = {
  value: 'card',
  title: 'Debit or credit card',
  subtitle: 'Enter card details on the next screen',
};
const offlineOption = {
  value: 'offline',
  title: 'Bill me later',
  subtitle: 'Pay by check after you register',
};
const options = [cardOption, offlineOption];
</script>

<style scoped>
.payment-options :deep(.v-selection-control-group) {
  gap: 12px;
}

.payment-option {
  position: relative;
  border: 1px solid var(--fundeasy-colors-neutral-300);
  border-radius: 4px;
  background-color: white;
}

.payment-option--selected {
  border-color: rgb(var(--v-theme-success));
  background-color: rgba(var(--v-theme-success), 0.06);
}

.payment-option--static {
  padding: 12px 16px;
}

.payment-option :deep(.v-label) {
  flex: 1;
  align-self: stretch;
  padding: 12px 16px 12px 4px;
  opacity: 1;
}

/* NOTE: The label's ::before covers the whole card, so a click anywhere on it selects the
   option, not only the circle or the text. The radio sits above it to keep its hover ripple. */
.payment-option :deep(.v-label)::before {
  content: '';
  position: absolute;
  inset: 0;
}

.payment-option :deep(.v-selection-control__wrapper) {
  position: relative;
  z-index: 1;
  margin-left: 8px;
}

.payment-option__title {
  color: var(--fundeasy-colors-neutral-900);
}
</style>
